import React, { useEffect, useState, useMemo } from "react";
import logo from "./assets/logo_savatevent.jpg";
import { 
  Layout, Users, Trophy, SwatchBook, Eye, Lock, Bell, Menu,
  CheckSquare, Settings, LogOut, Search, Info, ShoppingBag, Mail,
} from 'lucide-react'; 
import CategoryManager from "./components/CategoryManager";
import MatchBoard from "./components/MatchBoard";
import SettingsManager, { DEFAULT_FORMATS } from "./components/SettingsManager";
import UserSettings from "./components/UserSettings";
import OrderHistory from "./components/OrderHistory";
import ContactForm from "./components/ContactForm";
import FAQ from "./components/FAQ";
import AdminPanel from "./components/AdminPanel";
import PlateauManager from "./components/PlateauManager";
import ExportDocument from "./components/ExportDocument";
import LiveManagement from "./components/LiveManagement";
import CompetitionDetails from "./components/CompetitionDetails";
import AuthPage from "./components/AuthPage"; // Import de la page d'auth
import CompetitionDashboard from "./components/CompetitionDashboard"; // Import du dashboard de choix
import HomeDashboard from "./components/HomeDashboard";
import { generateInitialPoules, splitBalanced, generateFinalOrder } from "./utils/tournamentLogic";
import { fetchCategories, savePoulesApi, updateMatchWinnerApi, generateAndSaveMatchesApi,
    updateCompetitionDetails, fetchCompetitionAthletes, fetchCompetitionMatches, fetchCompetitionSettings, updateMatchOrderApi, updateCoManagersApi, updateCoOwnersApi, notifyCoManagerApi} from "./services/api";
import { supabase } from "./services/supabaseClient";
import { getUserCredits } from "./services/api";
import Swal from 'sweetalert2';
import { useMatchRealtime} from './hooks/useMatchRealTime.js';
import { useCompetitionActions } from './hooks/useCompetitionActions';
import Sidebar from "./components/Sidebar.js";


const MenuItem = ({ id, icon: Icon, label, activeTab, setActiveTab }) => (
  <div 
    onClick={() => setActiveTab(id)} 
    style={{ 
      padding: "14px 20px", cursor: "pointer", 
      backgroundColor: activeTab === id ? "#3182ce" : "transparent", 
      display: "flex", alignItems: "center", gap: "12px", fontSize: "14px", transition: "0.2s",
      color: "white"
    }}
  >
    <Icon size={18} /> {label}
  </div>
);

export default function MainApp({ session, selectedCompetition, onBackToDashboard, onSelectCompetition }) {
  const [categories, setCategories] = useState([]);
  const [order, setOrder] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [viewMode, setViewMode] = useState('poules'); 
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentRings, setCurrentRings] = useState(3);
  const [poulesData, setPoulesData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Permissions dérivées automatiquement — recalculées après refresh et après tout changement de comp/session
  const isOwner    = useMemo(() => !!selectedCompetition && selectedCompetition.owner_id === session?.user?.id, [selectedCompetition, session]);
  const isCoOwner  = useMemo(() => !!selectedCompetition && !isOwner && (selectedCompetition.co_owners || []).includes(session?.user?.email), [selectedCompetition, session, isOwner]);
  const isCoManager = useMemo(() => !!selectedCompetition && !isOwner && !isCoOwner && (selectedCompetition.co_managers || []).includes(session?.user?.email), [selectedCompetition, session, isOwner, isCoOwner]);
  const isReadOnly  = useMemo(() => !!selectedCompetition && !isOwner && !isCoOwner && !isCoManager, [selectedCompetition, isOwner, isCoOwner, isCoManager]);
  const [allAthletes, setAllAthletes] = useState([]);

  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!session?.user?.id) {
          setIsAdmin(false);
          return;
      }
      try {
        // En prod, on force la lecture depuis la table profiles avec un retry ou un fallback sur user_metadata
        const { data, error } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .maybeSingle();
        
        if (error) {
            console.error("Erreur API checkAdmin:", error);
            // Fallback sur les metadata de l'utilisateur (si synchronisées)
            setIsAdmin(session.user.app_metadata?.is_admin || session.user.user_metadata?.is_admin || false);
        } else {
            setIsAdmin(!!data?.is_admin);
        }
      } catch (err) {
        console.error("Erreur technique checkAdmin:", err);
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [session, session?.user?.id]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
const [showUserMenu, setShowUserMenu] = useState(false);

  // --- NOUVEAU : Persistance de la navigation et retour Stripe ---
  useEffect(() => {
    const initNavigation = async () => {
        const params = new URLSearchParams(window.location.search);
        
        if (params.get('session_id')) {
            setActiveTab('parametres');
            localStorage.setItem('savatevent_activeTab', 'parametres');
            return;
        }

        const savedTab = localStorage.getItem('savatevent_activeTab');
        const savedCompData = localStorage.getItem('savatevent_selectedComp');
        
        if (savedTab) {
            console.log("Restauration de l'onglet:", savedTab);
            setActiveTab(savedTab);
        }

        if (savedCompData) {
            try {
                const comp = JSON.parse(savedCompData);
                console.log("Restauration de la compétition:", comp.name);
                onSelectCompetition(comp);
            } catch (e) {
                console.error("Erreur recharge comp:", e);
            }
        }
    };

    initNavigation();
  }, []); // [] = exécution unique au montage

  // Sauvegarder l'onglet à chaque changement
  useEffect(() => {
    if (activeTab !== 'home' || localStorage.getItem('savatevent_activeTab')) {
        localStorage.setItem('savatevent_activeTab', activeTab);
    }
  }, [activeTab]);

  // Sauvegarder la compétition à chaque changement
  useEffect(() => {
    if (selectedCompetition) {
        localStorage.setItem('savatevent_selectedComp', JSON.stringify(selectedCompetition));
    } else {
        // Ne pas remove si on vient de rafraîchir (pour éviter la perte au chargement)
    }
  }, [selectedCompetition]);

  const [compInfo, setCompInfo] = useState({
        name: selectedCompetition?.name || '',
        address: '',
        date: '',
        startTime: '',
        weighInDate: '',
        weighInTime: ''
    });




  const [formatSettings, setFormatSettings] = useState(DEFAULT_FORMATS);

  const totalAthletes = useMemo(() => categories.reduce((sum, cat) => sum + (cat.athletes?.length || 0), 0), [categories]);
  const totalCategories = categories.length;
  const totalMatches = order.length;
  const sortCategories = (list) => [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

const loadInitialData = async () => {
    if (!selectedCompetition?.id) {
        setCategories([]);
        setPoulesData(null);
        setOrder([]);
        setAllAthletes([]);
        return;
    }

    try {
        const compId = selectedCompetition.id;

        // On rafraîchit les infos de la compétition pour avoir le rings_count à jour
        const { data: latestComp } = await supabase.from('competitions').select('*').eq('id', compId).maybeSingle();
        if (latestComp) {
            setCurrentRings(latestComp.rings_count || 3);
            onSelectCompetition(latestComp);
        }

        // 1. APPELS API (Nettoyés)
        const [settings, catData, mData, athletes] = await Promise.all([
            fetchCompetitionSettings(compId),
            fetchCategories(compId),
            fetchCompetitionMatches(compId),
            fetchCompetitionAthletes(compId)
        ]);

        // 2. GESTION DES SETTINGS
        let currentFormats = formatSettings;
        if (settings) {
            setFormatSettings(settings);
            currentFormats = settings;
        }

        // 3. GESTION DES ATHLÈTES (Le dictionnaire de noms)
        setAllAthletes(athletes);

        // 4. GESTION DES CATÉGORIES & PLATEAU
        if (catData) {
            const sorted = sortCategories(catData);
            setCategories(sorted);

            const initialPlateau = {};
            catData.forEach(cat => {
                if (cat.ring_id) {
                    const ringKey = `ring${cat.ring_id}`;
                    if (!initialPlateau[ringKey]) initialPlateau[ringKey] = [];
                    
                    const athletesCount = cat.athletes?.length || 0;
                    // 2 tireurs → toujours finale directe, quelle que soit la config
                    let rule = athletesCount === 2
                        ? { format: 'finale_directe', nb: 1 }
                        : (currentFormats[athletesCount.toString()] || (athletesCount >= 6 ? { format: 'poules', nb: Math.floor(athletesCount / 3) } : { format: 'poule_unique', nb: 1 }));

                    const poulesList = splitBalanced(cat.athletes || [], rule.nb);
                    poulesList.forEach((athletesList, i) => {
                        const isElim = rule.format === 'elimination' || rule.format === 'elimination_directe';
                        const isFinalDirecte = rule.format === 'finale_directe';
                        let pouleLabel;
                        if (isFinalDirecte) pouleLabel = "FINALE";
                        else if (isElim) pouleLabel = "Élimination Directe";
                        else if (poulesList.length > 1) pouleLabel = `Poule ${String.fromCharCode(65 + i)} (${athletesList.length})`;
                        else pouleLabel = "Poule Unique";
                        initialPlateau[ringKey].push({
                            id: `poule_${cat.id}_${i}`,
                            category_id: cat.id,
                            category_name: cat.name,
                            format: rule.format,
                            type: (isElim || isFinalDirecte) ? "elimination" : "poule",
                            poule_label: pouleLabel,
                            athletes: athletesList
                        });
                    });
                }
            });
            setPoulesData(initialPlateau);
        }

        // 5. GESTION DES MATCHS (Tri numérique)
        if (mData) {
            const sortedMatches = [...mData].sort((a, b) => {
                const numA = parseInt(String(a.match_ref).replace(/\D/g, '')) || 0;
                const numB = parseInt(String(b.match_ref).replace(/\D/g, '')) || 0;
                return numA - numB;
            });
            setOrder(sortedMatches);
        }

    } catch (error) { 
        console.error("Erreur lors du chargement initial :", error); 
    }
};

    const { 
    handleToggleLive, 
    handleUpdateWinner, 
    handleResetCompetition, 
    handleSaveAllSettings 
  } = useCompetitionActions({
    selectedCompetition,
    order,
    allAthletes,
    setOrder,
    setFormatSettings,
    onSelectCompetition,
    loadInitialData
  });

// --- EFFET 1 : Chargement des données au changement de compétition ---
useEffect(() => {
  loadInitialData();
}, [selectedCompetition?.id]); // Utilise .id pour être plus précis

    // Détection du redimensionnement
useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
}, []);

// Verrouillage du scroll arrière-plan quand le menu mobile est ouvert
useEffect(() => {
    if (isMobile && isMenuOpen) {
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
    } else {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
    }
    return () => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
    };
}, [isMobile, isMenuOpen]);

    // 3. TON NOUVEAU HOOK (C'est ici !)
  // Il remplace tout l'ancien bloc "useEffect" du Realtime
  useMatchRealtime(
    selectedCompetition,
    allAthletes,
    (payload) => {
      console.log("Changement Realtime reçu !", payload);
      loadInitialData();
    },
    (updatedComp) => {
      // Synchronise is_started (et tout changement sur la compétition) en temps réel
      onSelectCompetition({ ...selectedCompetition, ...updatedComp });
    }
  );

  const handleRefreshPoules = async () => {
    try {
      if (!categories.length) throw new Error("Aucune catégorie chargée.");
      Swal.fire({ title: 'Génération...', didOpen: () => Swal.showLoading() });

      // Sauvegarde du nombre de rings dans Supabase
      await updateCompetitionDetails(selectedCompetition.id, { rings_count: currentRings });

      const nouvelleStructure = generateInitialPoules(categories, formatSettings, currentRings);
      await savePoulesApi(nouvelleStructure);
      setPoulesData(nouvelleStructure);
      Swal.fire({ icon: 'success', title: 'Poules générées !', timer: 1500 });
    } catch (error) { Swal.fire({ icon: 'error', title: 'Erreur', text: error.message }); }
  };

    const handleSaveOrder = async (newOrder) => {
        try {
            await updateMatchOrderApi(newOrder);
            setOrder(newOrder);
            // On ne met pas de toast de succès ici pour ne pas polluer l'UI pendant le drag & drop
        } catch (error) {
            console.error("Erreur sauvegarde ordre:", error);
            Swal.fire('Erreur', "Impossible de sauvegarder l'ordre des matchs", 'error');
        }
    };

    const handleGenerateFinalMatches = async () => {
        const result = await Swal.fire({ 
            title: 'Générer les matchs ?', 
            text: "Cela remplacera les matchs existants pour cette compétition.",
            icon: 'question', 
            showCancelButton: true, 
            confirmButtonText: 'Oui, générer' 
        });

        if (result.isConfirmed) {
            try {
                Swal.showLoading();
                
                // 1. Génération de la logique (Front-end)
                const matchesToSave = generateFinalOrder(poulesData);
                
                // 2. Appel API externalisé (Back-end)
                const createdMatches = await generateAndSaveMatchesApi(matchesToSave, selectedCompetition.id);
                
                // 3. Mise à jour de l'état UI
                setOrder(createdMatches);
                
                Swal.fire({ icon: 'success', title: 'Matchs Validés !', timer: 2000 });
                setActiveTab('matchs');
                setViewMode('matchs');
            } catch (error) { 
                console.error("Erreur génération:", error);
                Swal.fire('Erreur', error.message, 'error'); 
            }
        }
    };

    

    

   

    

const handleCorrectMatch = async (match) => {
    // 1. Fonction de secours pour trouver le nom dans MainApp
    const findName = (id, placeholder) => {
        if (!id) return placeholder || "En attente";
        // On cherche dans allAthletes ou allAthletesFlat (selon le nom de ton state)
        const athlete = (allAthletes || []).find(a => String(a.id) === String(id));
        if (athlete) {
            const nom = (athlete.nom || athlete.name || "").toUpperCase();
            const prenom = athlete.prenom || athlete.firstname || "";
            return `${nom} ${prenom}`.trim();
        }
        return placeholder || "Athlète inconnu";
    };

    // 2. Préparation des noms pour la popup
    const name1 = findName(match.athlete1_id, match.placeholder1);
    const name2 = findName(match.athlete2_id, match.placeholder2);

    // 3. Affichage de la popup SweetAlert
    const { value: newWinner } = await Swal.fire({
        title: `Correction Assaut #${match.match_ref}`,
        text: "Sélectionnez le véritable vainqueur :",
        icon: 'question',
        width: '600px',
        showCancelButton: true,
        confirmButtonText: 'Valider',
        cancelButtonText: 'Annuler',
        input: 'radio',
        inputOptions: {
            '1': name1,
            '2': name2,
            'reset': 'Annuler le résultat'
        },
        // --- L'ASTUCE EST ICI ---
        didOpen: () => {
            // On récupère tous les labels des boutons radio dans la popup
            const labels = document.querySelectorAll('.swal2-radio label');
            labels.forEach(label => {
                label.style.display = 'flex';
                label.style.alignItems = 'center';
                label.style.fontSize = '18px'; // Plus lisible
                label.style.fontWeight = '500';
                label.style.whiteSpace = 'nowrap'; // Empêche le retour à la ligne
                label.style.margin = '10px 0';
                label.style.cursor = 'pointer';
            });

            // On peut aussi styliser le conteneur des radios
            const radioContainer = document.querySelector('.swal2-radio');
            if (radioContainer) {
                radioContainer.style.justifyContent = 'center';
                radioContainer.style.flexDirection = 'column';
                radioContainer.style.alignItems = 'flex-start';
                radioContainer.style.paddingLeft = '100px'; // Centre visuellement le bloc
            }
        },
        inputValidator: (value) => {
            if (!value) return 'Vous devez choisir une option';
        }
    });

    if (newWinner) {
        try {
            Swal.showLoading();
            let winnerId = null;
            if (newWinner === '1') winnerId = match.athlete1_id;
            if (newWinner === '2') winnerId = match.athlete2_id;

            // Préparation de la propagation (next_match)
            let nextMatchInfo = null;
            if (winnerId) {
                const labelRecherche = `Vainqueur V${match.match_ref}`;
                const nextMatch = order.find(m => 
                    m.category_name === match.category_name && 
                    (m.placeholder1 === labelRecherche || m.placeholder2 === labelRecherche)
                );

                if (nextMatch) {
                    nextMatchInfo = {
                        id: nextMatch.id,
                        data: nextMatch.placeholder1 === labelRecherche 
                            ? { athlete1_id: winnerId } 
                            : { athlete2_id: winnerId }
                    };
                }
            }

            // APPEL API CENTRALISÉ
            await updateMatchWinnerApi(match.id, winnerId, nextMatchInfo);

            await loadInitialData();
            Swal.fire('Succès', 'Le résultat a été corrigé', 'success');
        } catch (err) {
            Swal.fire('Erreur', err.message, 'error');
        }
    }
};

const handleUpdateCompetitionDetails = async (formData) => {
    // 1. Validation locale avant envoi
    if (!formData.name?.trim()) {
        Swal.fire({ icon: 'warning', title: 'Champ requis', text: 'Le nom de la compétition est obligatoire.' });
        return;
    }

    try {
        Swal.showLoading();
        // 2. Nettoyage des dates vides pour éviter l'erreur SQL
        const cleanData = { ...formData };
        if (cleanData.event_date === "") cleanData.event_date = null;
        if (cleanData.weigh_in_date === "") cleanData.weigh_in_date = null;
        if (cleanData.start_time === "") cleanData.start_time = null;
        if (cleanData.weigh_in_time === "") cleanData.weigh_in_time = null;

        const result = await updateCompetitionDetails(selectedCompetition.id, cleanData);
        
        if (typeof onSelectCompetition === 'function') {
            onSelectCompetition(result);
        }

        Swal.fire({
            icon: 'success',
            title: 'Configuration enregistrée',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
        });

    } catch (err) {
        console.error(err);
        let errorMsg = "Une erreur est survenue lors de la sauvegarde.";
        
        // On traduit l'erreur pour l'utilisateur sans mentionner Supabase
        if (err.message?.includes("invalid input syntax for type date")) {
            errorMsg = "Le format d'une des dates est incorrect.";
        }

        Swal.fire({
            icon: 'error',
            title: 'Erreur de sauvegarde',
            text: errorMsg,
            confirmButtonColor: '#2c5282'
        });
    }
};

const handleUpdateCoManagers = async (newList) => {
    const oldList = selectedCompetition.co_managers || [];
    const newlyAdded = newList.filter(e => !oldList.includes(e));
    try {
        const result = await updateCoManagersApi(selectedCompetition.id, newList);
        if (typeof onSelectCompetition === 'function') onSelectCompetition(result);
        newlyAdded.forEach(email => {
            notifyCoManagerApi(email, selectedCompetition.name, session?.user?.email).catch(() => {});
        });
    } catch (err) {
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de mettre à jour les gestionnaires.' });
    }
};

const handleUpdateCoOwners = async (newList) => {
    const oldList = selectedCompetition.co_owners || [];
    const newlyAdded = newList.filter(e => !oldList.includes(e));
    try {
        const result = await updateCoOwnersApi(selectedCompetition.id, newList);
        if (typeof onSelectCompetition === 'function') onSelectCompetition(result);
        newlyAdded.forEach(email => {
            notifyCoManagerApi(email, selectedCompetition.name, session?.user?.email).catch(() => {});
        });
    } catch (err) {
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de mettre à jour les co-propriétaires.' });
    }
};

return (
    <div style={{ display: "flex", height: isMobile ? 'auto' : "100dvh", minHeight: "100dvh", width: "100%", backgroundColor: "#f3f4f6", overflowX: 'hidden' }}>
      
      {/* 1. SIDEBAR (Facture.net style : contient tout sur mobile) */}
      <Sidebar 
        session={session}
        selectedCompetition={selectedCompetition}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onBackToDashboard={onBackToDashboard}
        isMobile={isMobile}
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        isAdmin={isAdmin}
      />

      {/* 2. OVERLAY MOBILE */}
      {isMobile && isMenuOpen && (
        <div
          onClick={() => setIsMenuOpen(false)}
          onTouchMove={e => e.preventDefault()}
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 999, touchAction: 'none' }}
        />
      )}

      {/* 3. CONTENU PRINCIPAL */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflowX: 'hidden' }}>
        
        {/* HEADER ÉPURÉ */}
        <header style={{
          height: "60px", backgroundColor: "white", borderBottom: "1px solid #e5e7eb",
          display: "flex", alignItems: "center", padding: "0 20px", justifyContent: "space-between",
          position: isMobile ? "sticky" : "relative",
          top: isMobile ? 0 : undefined,
          zIndex: isMobile ? 100 : undefined
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <button onClick={() => setIsMenuOpen(true)} style={{ background: 'none', border: 'none', marginRight: '15px', cursor: 'pointer' }}>
                <Menu size={24} color="#2c5282" />
              </button>
            )}
            <Search size={16} color="#9ca3af" />
          </div>

          {/* Avatar Profil : Uniquement sur PC (sur mobile il est dans la sidebar) */}
          {!isMobile && session && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ position: 'relative' }}>
                <div 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  title={session.user.email} // Ajout de l'email au survol
                style={{ 
                  width: "35px", height: "35px", backgroundColor: "#3182ce", 
                  borderRadius: "50%", color: 'white', display: 'flex', 
                  alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontWeight: 'bold' 
                }}
              >
                {session.user.email[0].toUpperCase()}
              </div>

              {showUserMenu && (
                <>
                  <div onClick={() => setShowUserMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                  <div style={{
                    position: 'absolute', top: '45px', right: 0, backgroundColor: 'white',
                    border: '1px solid #e2e8f0', borderRadius: '8px', width: '180px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 11, overflow: 'hidden'
                  }}>
                    <div onClick={() => { setActiveTab('parametres'); setShowUserMenu(false); }} style={{ padding: '10px 15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#4a5568' }}>
                      <Settings size={14}/> Réglages
                    </div>
                    <div onClick={() => { setActiveTab('commandes'); setShowUserMenu(false); }} style={{ padding: '10px 15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#4a5568' }}>
                      <ShoppingBag size={14}/> Mes Commandes
                    </div>
                    <div onClick={() => { setActiveTab('contact'); setShowUserMenu(false); }} style={{ padding: '10px 15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#4a5568' }}>
                      <Mail size={14}/> Nous contacter
                    </div>
                    <div onClick={() => { supabase.auth.signOut(); setShowUserMenu(false); }} style={{ padding: '10px 15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#e53e3e', borderTop: '1px solid #edf2f7' }}>
                      <LogOut size={14}/> Déconnexion
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          )}
        </header>

        <main style={{ padding: isMobile ? "15px" : "25px", overflowY: "auto", overflowX: "hidden", flex: isMobile ? "none" : 1, paddingBottom: isMobile ? "calc(env(safe-area-inset-bottom) + 40px)" : "25px", boxSizing: 'border-box', width: '100%' }}>
          {!session ? (
            <div style={{ textAlign: 'center', marginTop: '50px' }}>
              <h2>Connectez-vous à SavatEvent</h2>
              <div style={{ maxWidth: '400px', margin: '20px auto' }}><AuthPage /></div>
            </div>
          ) : (
            <>
              {/* ACCUEIL */}
              {activeTab === 'home' && (
                <HomeDashboard 
                  user={session?.user} 
                  onSelect={(comp) => {
                    onSelectCompetition(comp);
                    setActiveTab('dashboard');
                  }}
                  onNavigate={setActiveTab}
                  onShowLogin={() => {}} // À gérer si besoin d'ouvrir la modale
                />
              )}

              {/* PARAMETRES UTILISATEUR - Accessibles à tout moment */}
              {activeTab === 'parametres' && (
                <UserSettings session={session} />
              )}

              {activeTab === 'commandes' && (
                <OrderHistory userId={session.user.id} />
              )}

              {activeTab === 'contact' && (
                <ContactForm user={session.user} />
              )}

              {activeTab === 'faq' && (
                <FAQ onNavigate={setActiveTab} />
              )}

              {activeTab === 'admin_panel' && isAdmin && (
                <AdminPanel
                  onSelectCompetition={(comp) => {
                    onSelectCompetition(comp);
                    setActiveTab('dashboard');
                  }}
                />
              )}

              {/* DASHBOARD DE CHOIX */}
              {(activeTab === 'public_list' || activeTab === 'list_competitions' || (!selectedCompetition && !['parametres', 'commandes', 'home', 'contact', 'faq', 'admin_panel'].includes(activeTab))) && (
                <CompetitionDashboard
                  user={session.user}
                  mode={activeTab === 'public_list' ? "public" : "admin"}
                  onSelect={(comp) => {
                    onSelectCompetition(comp);
                    setActiveTab('dashboard');
                  }}
                  onNavigate={setActiveTab}
                />
              )}

              {/* VUES D'UNE COMPÉTITION */}
              {selectedCompetition && !['public_list', 'list_competitions'].includes(activeTab) && (
                <>
                  {isReadOnly && (
                    <div style={{ backgroundColor: '#ebf8ff', color: '#2b6cb0', padding: '12px 20px', marginBottom: '20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold' }}>
                      <Eye size={18} /> Mode Spectateur
                    </div>
                  )}
                  {isCoOwner && (
                    <div style={{ backgroundColor: '#faf5ff', color: '#5b21b6', padding: '12px 20px', marginBottom: '20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold', border: '1px solid #d6bcfa' }}>
                      <CheckSquare size={18} /> Co-propriétaire — accès complet sauf suppression
                    </div>
                  )}
                  {isCoManager && !isCoOwner && !isReadOnly && (
                    <div style={{ backgroundColor: '#ebf8ff', color: '#2b6cb0', padding: '12px 20px', marginBottom: '20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold' }}>
                      <Users size={18} /> Co-organisateur — saisie des résultats uniquement
                    </div>
                  )}

                  {activeTab === 'info_generale' && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
                      {selectedCompetition?.is_started && !isReadOnly && (
                          <div style={{ backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030', padding: '12px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          ⚠️ La compétition est en cours. La structure est verrouillée.
                          </div>
                      )}
                      <CompetitionDetails
                        competition={selectedCompetition}
                        onUpdate={handleUpdateCompetitionDetails}
                        onUpdateCoManagers={handleUpdateCoManagers}
                        onUpdateCoOwners={handleUpdateCoOwners}
                        onDelete={() => {
                          onBackToDashboard();
                          setActiveTab('home');
                        }}
                        isOwner={isOwner}
                        isCoOwner={isCoOwner}
                        isCoManager={isCoManager}
                        readOnly={isReadOnly || isCoManager || selectedCompetition?.is_started}
                        currentUserEmail={session?.user?.email || ''}
                      />
                    </div>
                  )}

                  {activeTab === 'dashboard' && (
                    <LiveManagement
                      order={order} allAthletes={allAthletes} categories={categories} selectedCompetition={selectedCompetition}
                      onToggleLive={(isOwner || isCoOwner) ? handleToggleLive : null}
                      onUpdateWinner={(!isReadOnly) ? handleUpdateWinner : null}
                      onReset={isOwner ? handleResetCompetition : null}
                      onCorrectMatch={(!isReadOnly) ? handleCorrectMatch : null}
                      readOnly={isReadOnly}
                      stats={{ totalAthletes, totalCategories, totalMatches, competitionName: selectedCompetition.name }}
                    />
                  )}

                {/* 3. ONGLETS ADMINISTRATIFS (Propriétaire et co-propriétaire) */}
                {(isOwner || isCoOwner) ? (
                    <>
                    {(activeTab === 'athletes' || activeTab === 'categories') && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
                        {selectedCompetition?.is_started && (
                            <div style={{ backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030', padding: '12px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            ⚠️ La compétition est en cours. La structure est verrouillée.
                            </div>
                        )}
                        <CategoryManager 
                            competitionId={selectedCompetition.id}
                            categories={sortCategories(categories)}
                            onAddCategory={(newList) => {
                                const cats = Array.isArray(newList) ? newList : [newList];
                                setCategories(sortCategories([...categories, ...cats]));
                            }}
                            onDeleteCategory={(id) => setCategories(categories.filter(c => c.id !== id))}
                            onSelectCategory={(c) => { 
                                // On ne change plus d'onglet, on reste ici
                                const newExpanded = new Set();
                                newExpanded.add(c.id);
                                // On pourrait passer ça en prop à CategoryManager si on voulait piloter l'ouverture
                            }}
                            loadInitialData={loadInitialData}
                            readOnly={selectedCompetition?.is_started}
                        />
                        </div>
                    )}

                    {activeTab === 'matchs' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {selectedCompetition?.is_started && (
                            <div style={{ backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030', padding: '12px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            ⚠️ La compétition est en cours. La structure est verrouillée.
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '5px', backgroundColor: '#edf2f7', padding: '5px', borderRadius: '8px', width: isMobile ? '100%' : 'fit-content' }}>
                            {['poules', 'matchs', 'export'].map(m => (
                            <button
                                key={m}
                                onClick={() => setViewMode(m)}
                                style={{ flex: isMobile ? 1 : 'none', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: viewMode === m ? 'white' : 'transparent', fontWeight: viewMode === m ? 'bold' : 'normal', fontSize: isMobile ? '13px' : '14px' }}
                            >
                                {m.charAt(0).toUpperCase() + m.slice(1)}
                            </button>
                            ))}
                        </div>

                        {viewMode === 'poules' && (
                            <PlateauManager 
                            plateau={poulesData} 
                            onGenerate={selectedCompetition?.is_started ? null : handleRefreshPoules} 
                            onUpdatePlateau={selectedCompetition?.is_started ? () => {} : setPoulesData} 
                            onFinalGenerate={selectedCompetition?.is_started ? null : handleGenerateFinalMatches} 
                            currentRings={currentRings} 
                            setCurrentRings={setCurrentRings} 
                            readOnly={selectedCompetition?.is_started}
                            />
                        )}

                        {viewMode === 'matchs' && (
                            <MatchBoard
                            order={order}
                            currentRings={currentRings}
                            onUpdateOrder={selectedCompetition?.is_started ? () => {} : handleSaveOrder}
                            onGenerate={selectedCompetition?.is_started ? null : handleGenerateFinalMatches}
                            allAthletes={allAthletes}
                            categories={categories}
                            readOnly={selectedCompetition.is_started}
                            isMobile={isMobile}
                            />
                        )}

                        {viewMode === 'export' && (
                            <ExportDocument 
                            order={order} 
                            currentRings={currentRings} 
                            allAthletes={allAthletes} 
                            />
                        )}
                        </div>
                    )}

                    {activeTab === 'parametres_comp' && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
                        {selectedCompetition?.is_started && (
                            <div style={{ backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030', padding: '12px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            ⚠️ La compétition est en cours. La structure est verrouillée.
                            </div>
                        )}
                        <SettingsManager settings={formatSettings} onUpdate={handleSaveAllSettings} readOnly={selectedCompetition?.is_started} />
                        </div>
                    )}

                    </>
                ) : (
                    /* SI CO-MANAGER OU SPECTATEUR : Message pour les onglets interdits */
                    ['athletes', 'categories', 'matchs', 'parametres_comp'].includes(activeTab) && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#718096' }}>
                        <Lock size={48} style={{ marginBottom: '20px' }} />
                        <h3>Accès Restreint</h3>
                        <p>{isCoManager
                            ? "En tant que co-organisateur, vous pouvez uniquement saisir les résultats des matchs depuis l'onglet Direct."
                            : "Vous consultez cette compétition en tant que spectateur. Seul l'organisateur peut accéder à ces paramètres."
                        }</p>
                        <button
                        onClick={() => setActiveTab('dashboard')}
                        style={{ backgroundColor: '#3182ce', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', marginTop: '10px', cursor: 'pointer' }}
                        >
                        Retour au Direct
                        </button>
                    </div>
                    )
                )}

                </>
            )}
            </>
        )}
        </main>
      </div>
    </div>
  );
}
